import React, { createContext, useContext, useState, useCallback } from "react";

// ── Global dataset store ──────────────────────────────────────────────────────
// One shared store. When a dataset is uploaded on ANY page, it's available on ALL pages.
// User can upload a NEW dataset on any page to replace it.
// Data lives in memory only — cleared when the browser tab is closed.

const DataContext = createContext(null);

const EMPTY = { data: [], columns: [], fileName: "", file: null, availableCols: [], result: null };

export function DataProvider({ children }) {
  // Single shared dataset — same data everywhere
  const [shared, setShared] = useState({ ...EMPTY });

  // Per-page analysis results (Dashboard ML result stays on Dashboard)
  const [pageResults, setPageResults] = useState({});

  const setDataset = useCallback((payload) => {
    setShared(prev => ({ ...prev, ...payload }));
  }, []);

  const setPageResult = useCallback((page, result) => {
    setPageResults(prev => ({ ...prev, [page]: result }));
  }, []);

  const clearAll = useCallback(() => {
    setShared({ ...EMPTY });
    setPageResults({});
  }, []);

  return (
    <DataContext.Provider value={{ shared, setDataset, pageResults, setPageResult, clearAll }}>
      {children}
    </DataContext.Provider>
  );
}

export function useSharedDataset() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useSharedDataset must be inside DataProvider");
  return {
    dataset:       ctx.shared,
    setDataset:    ctx.setDataset,
    pageResults:   ctx.pageResults,
    setPageResult: ctx.setPageResult,
    clearAll:      ctx.clearAll,
    hasData:       ctx.shared.data.length > 0,
  };
}

export default DataContext;
